import { faBarcode, faPrint, faQrcode, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence, motion } from 'framer-motion';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePanelVisibility } from 'src/hooks/usePanelVisibility';
import { BUREAU_BARCODE_FORMAT, LOCALSTORAGE_KEYS } from 'src/utils/constants';
import routes from 'src/utils/routes';
import { isRouteAllowed } from 'src/utils/url';

import useLocalstorage from '../utils/useLocalStorage';

const ALLOWED_ACTION_BUTTON_ROUTES = [
  routes.materialContainer,
  routes.permanentContainer,
  routes.printerMaterial,
  routes.materialContainerDetails,
  routes.permanentContainerDetails,
];

const FloatingScanButton = ({ onPrintQRCodesClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const handlePrintClick = () => {
    if (onPrintQRCodesClick) {
      onPrintQRCodesClick();
    }
  };

  const { pathname } = location;

  const isActionButtonVisible = useMemo(
    () => isRouteAllowed(pathname, ALLOWED_ACTION_BUTTON_ROUTES),
    [pathname]
  );

  const toggleOpen = () => {
    setIsOpen(prev => !prev);
  };

  const stackContainerVariants = {
    hidden: { opacity: 0, scale: 0 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0 },
  };

  const buttonVariants = {
    hidden: { opacity: 0, scale: 0 },
    visible: {
      opacity: 1,
      scale: 1,
    },
    exit: { opacity: 0, scale: 0 },
  };

  const qrCodeBtnVariant = {
    ...buttonVariants,
    visible: {
      ...buttonVariants.visible,
      transition: { delay: 0.1 },
    },
  };

  const printBtnVariant = {
    ...buttonVariants,
    visible: {
      ...buttonVariants.visible,
      transition: { delay: 0.2 },
    },
  };

  const { isPanelVisible, isPageScrollable, isScrolledDown, routeAllowed } = usePanelVisibility();

  const onPrintClick = () => {
    handlePrintClick();
    setIsOpen(false);
  };

  const getScanButtonAnimationClass = () => {
    if (!routeAllowed) return 'hidden';
    if (isScrolledDown) return 'onScrollDown';
    if (!isPanelVisible || !isPageScrollable) return 'hidden';
    return 'visible';
  };

  const [scanMode] = useLocalstorage(LOCALSTORAGE_KEYS.SCAN_MODE, BUREAU_BARCODE_FORMAT.QR);
  const scanIcon = scanMode === BUREAU_BARCODE_FORMAT.BARCODE ? faBarcode : faQrcode;

  return isActionButtonVisible ? (
    <div className='floating-button-container'>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className='floating-button-stack'
            initial='hidden'
            animate='visible'
            exit='exit'
            variants={stackContainerVariants}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className='floating-button'
              initial='hidden'
              animate='visible'
              exit='exit'
              variants={printBtnVariant}
              transition={{ duration: 0.3 }}
              data-cy='floating-scan-print-btn'
              onClick={onPrintClick}
            >
              <FontAwesomeIcon icon={faPrint} />
            </motion.div>
            <motion.div
              className='floating-button'
              initial='hidden'
              animate='visible'
              exit='exit'
              variants={qrCodeBtnVariant}
              transition={{ duration: 0.3 }}
            >
              <Link to={routes.scan} data-cy='floating-scan-qr-link'>
                <FontAwesomeIcon icon={scanIcon} />
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <div
        className={`floating-button ${isOpen ? 'close' : ''}`}
        data-cy='floating-scan-toggle-btn'
        onClick={toggleOpen}
      >
        <FontAwesomeIcon icon={isOpen ? faXmark : scanIcon} />
      </div>
    </div>
  ) : (
    <Link to={routes.scan}>
      <button
        type='button'
        className={`btn text-reset text-white scan-btn ${getScanButtonAnimationClass()}`}
        aria-label='New Scan'
        data-cy='floating-scan-btn'
      >
        <FontAwesomeIcon icon={scanIcon} color='#fff' />
      </button>
    </Link>
  );
};

FloatingScanButton.defaultProps = {
  onPrintQRCodesClick: () => null,
};

FloatingScanButton.propTypes = {
  onPrintQRCodesClick: PropTypes.func,
};

export default FloatingScanButton;
